import { LuPin as Pin, LuPinOff as PinOff } from 'react-icons/lu';
import { Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import './opportunityChat.scss';
import { formatDateTime } from 'src/utils';
import i18next from 'i18next';
import * as Utils from './opportunityChatUtils';
import { t } from 'i18next';
import { CSSProperties } from 'react';

export type Message = {
    id: string;
    sender: string;
    text: string;
    time: string;
    isOwn: boolean;
    avatar?: string | React.ReactNode;
    pinned?: boolean
}

type TextMessageProps = {
    message: Message;
    pinnedMessages: Message[];
    handleTogglePin: (messageId: string) => void;
    messageRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
    pulsatingMessageId: string | null | undefined;
    canPinMessages?: boolean
}

export const TextMessage: React.FC<TextMessageProps> = ({
    message,
    pinnedMessages,
    handleTogglePin,
    messageRefs,
    pulsatingMessageId,
    canPinMessages
}) => {
    const isPinned = pinnedMessages.some(pm => pm.id === message.id);
    const isPulsating = pulsatingMessageId === message.id;

    const wrapperStyle: CSSProperties = {
        position: 'relative',
        wordWrap: 'break-word',

    };

    const containerStyle: CSSProperties = {
        padding: '10px 14px',
        borderRadius: message.isOwn ? '15px 15px 3px 15px' : '15px 15px 15px 3px',
        backgroundColor: message.isOwn ? '#dcf8c6' : '#ffffff',
        color: '#1c1e21',
        boxShadow: '0 1px 0.5px rgba(0, 0, 0, 0.13)',
        position: 'relative',
    };

    const pinButtonStyle: CSSProperties = {
        position: 'absolute',
        top: '-19px',
        left: message.isOwn ? '-25px' : 'auto',
        right: message.isOwn ? 'auto' : '-25px',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: isPinned ? '#856404' : '#6c757d',
        zIndex: 2,
    };

    const avatarStyle: CSSProperties = {
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        backgroundColor: '#6c757d',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
    };

    return (
        <div
            ref={el => messageRefs.current[message.id] = el}
            className={`d-flex mb-3 ${message.isOwn ? 'justify-content-end' : 'justify-content-start'} ${isPulsating ? 'pulsate-background' : ''}`}
        >
            {!message.isOwn && (
                <div className="me-2" style={avatarStyle}>
                    {message.avatar}
                </div>
            )}

            <div className="d-flex flex-column align-items-start">
                {!message.isOwn && (
                    <small className="text-muted mb-1 ps-2 fw-medium">
                        {Utils.getAuthorInitials(message.sender)}
                    </small>
                )}

                <div style={wrapperStyle}>
                    {/* Message text */}
                    <div style={containerStyle}>
                        <span style={{ whiteSpace: 'pre-line' }}>{message.text}</span>
                    </div>

                    {/* Pin/unpin button */}
                    {isPinned || canPinMessages ? (
                        <OverlayTrigger
                            placement="top"
                            trigger={["click", "hover"]}
                            overlay={<Tooltip style={{ position: "fixed" }}>{isPinned ? t('unpin_message') : t('pin_message')}</Tooltip>}
                        >
                            <Button
                                variant="link"
                                disabled={!canPinMessages}
                                style={pinButtonStyle}
                                onClick={() => handleTogglePin(message.id)}
                            >
                                {isPinned ? <PinOff size={18} /> : <Pin size={18} />}
                            </Button>
                        </OverlayTrigger>
                    ) : null}
                </div>

                {/* Author */}
                <div className={`d-flex align-items-center mt-1 ${message.isOwn ? 'flex-row-reverse' : ''}`}>
                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                        {formatDateTime(message.time, i18next.language)}
                    </small>
                    {message.isOwn && (
                        <small className="text-muted mx-1" style={{ fontSize: '0.75rem' }}>{t('you')}</small>
                    )}
                </div>
            </div>

            {/* Avatar */}
            {message.isOwn && (
                <div className="ms-2" style={avatarStyle}>
                    {message.avatar}
                </div>
            )}
        </div>
    );
};
